/** Cmd/Ctrl+K command palette: autocomplete suggestions, hybrid search, recent docs, quick actions */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import type { SuggestItem } from '@agentwiki/shared'
import { API_BASE } from '../../lib/api-client'
import { buildCreateDocumentBody, useDocuments, useCreateDocument } from '../../hooks/use-documents'
import { useSearch, useSuggest } from '../../hooks/use-search'
import { useDebounce } from '../../hooks/use-debounce'
import { useCreateFolder } from '../../hooks/use-folders'
import { useAppStore } from '../../stores/app-store'
import { getWebFeatureProfile } from '../../lib/feature-flags'
import { cn } from '../../lib/utils'
import { useOpenDocumentRoute, type RoutableDocument } from '../../hooks/use-open-document-route'
import { buildGlobalSearchPath } from '../../lib/global-search-route'
import { CommandPaletteInput } from './command-palette-input'
import { CommandPaletteList } from './command-palette-list'
import { useBodyScrollLock } from '../../hooks/use-body-scroll-lock'
import { useAuth } from '../../hooks/use-auth'

const PALETTE_FOCUSABLE =
  'input:not([disabled]), button:not([disabled]), [cmdk-item]:not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])'

export function CommandPalette() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query.trim(), 250)
  const debouncedSuggestQuery = useDebounce(query.trim(), 150)

  const {
    theme,
    openTab,
    setActiveTab,
    commandPaletteOpen: open,
    setCommandPaletteOpen: setOpen,
  } = useAppStore()
  const navigate = useNavigate()
  const openDocumentRoute = useOpenDocumentRoute()
  const dialogRef = useRef<HTMLDivElement>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  useBodyScrollLock(open)

  // Autocomplete suggestions (faster debounce, >= 1 char)
  const { data: suggestData, isPlaceholderData: isSuggestPlaceholder } =
    useSuggest(debouncedSuggestQuery)
  // Hybrid search for typed queries (>= 2 chars)
  const {
    data: searchData,
    isFetching: isSearching,
    isPlaceholderData: isSearchPlaceholder,
  } = useSearch(debouncedQuery)
  // Recent docs when no query
  const { data: recentData } = useDocuments({ limit: 5 })
  const createDocument = useCreateDocument()
  const createFolder = useCreateFolder()
  const { role } = useAuth()
  const canCreate = role === 'admin' || role === 'editor'

  const isDark = theme === 'dark'

  // Toggle on Ctrl+K / Cmd+K
  const toggleCommandPalette = useAppStore((s) => s.toggleCommandPalette)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggleCommandPalette()
      }
      if (e.key === 'Escape') setOpen(false)
    },
    [toggleCommandPalette, setOpen],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    if (open) return
    if (document.activeElement instanceof HTMLElement) {
      restoreFocusRef.current = document.activeElement
    }
    const rememberFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && !dialogRef.current?.contains(event.target)) {
        restoreFocusRef.current = event.target
      }
    }
    document.addEventListener('focusin', rememberFocus)
    return () => document.removeEventListener('focusin', rememberFocus)
  }, [open])

  useEffect(() => {
    if (!open) return
    if (!restoreFocusRef.current && document.activeElement instanceof HTMLElement) {
      restoreFocusRef.current = document.activeElement
    }
    const shell = document.querySelector<HTMLElement>('.aw-app-shell')
    shell?.setAttribute('aria-hidden', 'true')
    if (shell) shell.inert = true
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus()

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(PALETTE_FOCUSABLE) ?? [],
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', trapFocus)
    return () => {
      document.removeEventListener('keydown', trapFocus)
      shell?.removeAttribute('aria-hidden')
      if (shell) shell.inert = false
      restoreFocusRef.current?.focus()
      restoreFocusRef.current = null
    }
  }, [open])

  const openDocument = (doc: RoutableDocument) => {
    openDocumentRoute(doc)
    setOpen(false)
    setQuery('')
  }

  /** Open upload result — navigate to file URL directly */
  const openUpload = (result: { fileUrl?: string }) => {
    if (result.fileUrl) {
      window.open(`${API_BASE}${result.fileUrl}`, '_blank')
    }
    setOpen(false)
    setQuery('')
  }

  const handleSuggestionClick = (item: SuggestItem) => {
    if (item.documentId && item.slug) {
      // Title or fuzzy suggestion — navigate directly
      openDocument({ id: item.documentId, title: item.text, slug: item.slug })
    } else {
      // History suggestion — fill search input to trigger full search
      setQuery(item.text)
    }
  }

  const handleNewDoc = async () => {
    if (!canCreate) return
    setOpen(false)
    try {
      const doc = await createDocument.mutateAsync(
        buildCreateDocumentBody({
          title: query.trim() || 'Untitled',
          source: 'the AgentWiki command palette',
        }),
      )
      const tabId = `tab-${doc.id}`
      openTab({ id: tabId, documentId: doc.id, title: doc.title })
      setActiveTab(tabId)
      navigate(`/doc/${doc.slug}/edit`)
    } catch (err) {
      console.error('Failed to create document:', err)
    }
    setQuery('')
  }

  const handleNewFolder = async () => {
    if (!canCreate) return
    setOpen(false)
    const name = query.trim() || 'New Folder'
    try {
      await createFolder.mutateAsync({ name })
    } catch (err) {
      console.error('Failed to create folder:', err)
    }
    setQuery('')
  }

  const handleOpenFullSearch = () => {
    navigate(buildGlobalSearchPath(query, getWebFeatureProfile().storageSpace))
    setOpen(false)
    setQuery('')
  }

  if (!open) return null

  const trimmedQuery = query.trim()
  const suggestionsActive =
    trimmedQuery === debouncedSuggestQuery &&
    suggestData?.query === debouncedSuggestQuery &&
    !isSuggestPlaceholder
  const searchActive =
    trimmedQuery === debouncedQuery && searchData?.query === debouncedQuery && !isSearchPlaceholder
  const suggestions = suggestionsActive ? suggestData.suggestions : []
  const searchResults = searchActive ? searchData.results : []
  const recentDocs = recentData?.data ?? []
  const showSuggestions = debouncedSuggestQuery.length >= 1 && suggestions.length > 0
  const showSearch = debouncedQuery.length >= 2 && searchResults.length > 0
  const showRecent = !showSuggestions && !showSearch && recentDocs.length > 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[10vh] md:px-0 md:pt-[20vh]"
      onClick={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Panel */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-2xl border shadow-2xl',
          isDark ? 'border-white/[0.08] bg-surface-2' : 'border-neutral-200 bg-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandPaletteInput
            query={query}
            isDark={isDark}
            isSearching={isSearching}
            onQueryChange={setQuery}
            onClose={() => setOpen(false)}
          />
          <CommandPaletteList
            query={query}
            isDark={isDark}
            isSearching={isSearching}
            suggestions={suggestions}
            searchResults={searchResults}
            recentDocuments={recentDocs}
            showSuggestions={showSuggestions}
            showSearch={showSearch}
            showRecent={showRecent}
            canCreateDocument={canCreate}
            canCreateFolder={canCreate}
            onNewDocument={handleNewDoc}
            onNewFolder={handleNewFolder}
            onOpenFullSearch={handleOpenFullSearch}
            onSuggestion={handleSuggestionClick}
            onOpenUpload={openUpload}
            onOpenSearchResult={openDocument}
            onOpenRecentDocument={openDocument}
          />

          {/* Footer hint */}
          <div
            className={cn(
              'flex justify-end border-t px-3 py-2',
              isDark ? 'border-white/[0.06]' : 'border-neutral-200',
            )}
          >
            <span className={cn('text-[11px]', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
              ↑↓ navigate · Enter select · Esc close
            </span>
          </div>
        </Command>
      </div>
    </div>
  )
}
